/**
 * Validación del contacto personal que el admin registra al dar de baja a un empleado
 * (tarea "Captura de datos personales en el flujo de baja").
 */
import { validateEmail } from './employeeInviteValidation';

// Formato aceptado por el diseño: dígitos con prefijo `+`, espacios o guiones opcionales.
const PHONE_FORMAT = /^\+?[0-9\s-]+$/;

// Cantidad de dígitos REALES admitidos. Se cuentan aparte del formato porque los
// separadores no son longitud: `6-0-0-1-2` tiene 9 caracteres pero solo 5 dígitos, y
// guardarlo dejaría un contacto imposible de marcar.
const MIN_PHONE_DIGITS = 9;
const MAX_PHONE_DIGITS = 15;

// Números de relleno: un teléfono así deja un contacto inservible, que es justo lo que
// esta captura pretende evitar. Se comparan los dígitos nacionales, para que
// `+34 000 000 000` caiga igual que `000000000`.
const REPEATED_DIGITS = /^(\d)\1+$/; // 000000000, 111111111…
// Lista EXACTA, no "parecido a una secuencia": `678901234` y `789012345` son móviles
// españoles corrientes y comprobar si el número es subcadena de 123456789012345 los
// rechazaba, dejando al empleado sin su teléfono real.
const SEQUENTIAL_FILLERS = ['123456789', '987654321'];

const digitsOf = (value: string): string => value.replace(/\D/g, '');

export const validatePhone = (phone?: string): boolean => {
	const trimmed = phone?.trim();
	if (!trimmed || !PHONE_FORMAT.test(trimmed)) return false;

	const digits = digitsOf(trimmed);
	if (digits.length < MIN_PHONE_DIGITS || digits.length > MAX_PHONE_DIGITS) return false;

	// El número nacional (últimos 9 dígitos): con el prefijo delante, el `34` de
	// `+34 000 000 000` disimularía un relleno que sin prefijo sí se detecta.
	const national = digits.slice(-MIN_PHONE_DIGITS);
	if (REPEATED_DIGITS.test(national)) return false;
	if (SEQUENTIAL_FILLERS.includes(national)) return false;

	return true;
};

// Longitud máxima habitual de una dirección de correo, la misma que valida el backend.
const MAX_EMAIL_LENGTH = 254;
// Puntos al principio o al final del dominio, y puntos consecutivos: la regex del diseño
// los deja pasar, pero el `@IsEmail` del backend no. Sin esta comprobación el correo se
// envía, el servidor lo rechaza y el admin recibe un error genérico que no señala el campo.
const MALFORMED_DOMAIN_DOTS = /@\.|\.@|\.\.|\.$/;

export const validatePersonalEmail = (email?: string): boolean => {
	const trimmed = email?.trim();
	if (!trimmed || trimmed.length > MAX_EMAIL_LENGTH) return false;

	return validateEmail(trimmed) && !MALFORMED_DOMAIN_DOTS.test(trimmed);
};
